import {
  Button,
  Disclaimer,
  Div,
  Icon,
  InputSearchIcon,
  Label,
  lightTheme,
} from '@vgrubs/components';
import styled from 'styled-components';
import { forwardRef, useId, useState, useRef } from 'react';

type AdminInputProps = {
  onXButtonClick?: (e: React.MouseEvent<HTMLElement>) => void;
  shadowDesign?: boolean;
  search?: boolean;
  $textCenter?: boolean;
  $password?: boolean;
  $textareaHeight?: number | string;
  suffix?: string;
  $hasSuffix?: boolean;
  $resizable?: boolean;
};

const AdminInputStyled = styled.input<AdminInputProps>`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.darkGray}80;
  border-radius: 200px;
  font-size: 15px;
  line-height: 18px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  height: ${({ theme }) => theme.input.height};
  -moz-appearance: none; /* Firefox */
  -webkit-appearance: none; /* Safari and Chrome */
  appearance: none;
  background: none;

  :disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  ${({ shadowDesign, theme }) =>
    shadowDesign &&
    `
  background: ${theme.white};
  border: 1px solid #e3e3e3;
  box-shadow: ${theme.shadows.shadow1};

  `}

  ${({ search }) => search && `padding-right: 50px`};
  ${({ $hasSuffix }) => $hasSuffix && `padding-right: 52px`};
  ${({ $textCenter }) => $textCenter && `text-align: center`};

  :focus {
    outline: none !important;
    border: 1px solid ${({ theme }) => theme.primary};
  }

  ${({ $password }) =>
    $password &&
    `

  text-security: disc;
  -webkit-text-security: disc;
  -mox-text-security: disc;
  
  `};

  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    margin: 0;
  }

  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
`;

type AdminTextAreaProps = {
  $textareaHeight?: number | string;
  shadowDesign?: boolean;
  $textCenter?: boolean;
  $resizable?: boolean;
};

const AdminTextAreaStyled = styled.textarea<AdminTextAreaProps>`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.darkGray}80;
  border-radius: 20px;
  font-size: 15px;
  line-height: 18px;
  padding: 12px 20px;
  display: flex;
  min-width: 100%;
  max-width: 100%;
  min-height: ${({ $textareaHeight }) => $textareaHeight ?? 300}px;
  max-height: ${({ $textareaHeight }) => $textareaHeight ?? 300}px;
  height: ${({ $textareaHeight }) => $textareaHeight ?? 300}px;
  font-family: inherit;
  /* padding: 16px; */
  /* align-items: center; */
  -moz-appearance: none; /* Firefox */
  -webkit-appearance: none; /* Safari and Chrome */
  appearance: none;
  background: none;

  :disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  ${({ shadowDesign, theme }) =>
    shadowDesign &&
    `
  background: ${theme.white};
  border: 1px solid #e3e3e3;
  box-shadow: ${theme.shadows.shadow1};

  `}

  ${({ $textCenter }) => $textCenter && `text-align: center`};

  :focus {
    outline: none !important;
    border: 1px solid ${({ theme }) => theme.primary};
  }

  ${({ $resizable }) =>
    $resizable &&
    `
    max-height: none;
    resize: vertical;
  `}

  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
`;

const FileInputStyled = styled.input`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 2;

  &::-webkit-file-upload-button {
    display: none;
  }

  &::file-selector-button {
    display: none;
  }

  &:focus + div {
    border: 1px solid ${({ theme }) => theme.primary};
  }
`;

const FileInputWrapper = styled.div`
  position: relative;
  width: 100%;
  cursor: pointer;
`;

type BaseProps = AdminInputProps & {
  maxWidth?: string;
  maxWidthSM?: string;
  mb?: string;
  label?: string;
  tooltip?: string;
  maxLength?: number;
  noMaxLengthLabel?: boolean;
  showCharCount?: boolean;
  tooltipWidth?: string;
  tooltipWidthSM?: string;
};

type TextareaProps = BaseProps &
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    type: 'textarea';
  };

type InputProps = BaseProps & React.InputHTMLAttributes<HTMLInputElement>;

export type AdminInputComponentProps = TextareaProps | InputProps;

const SuffixWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SuffixSpan = styled.span<{
  $value: string;
  $isInputFocused: boolean;
}>`
  position: absolute;
  right: ${({ $value }) => {
    const value = Number($value);
    if (value > 99) {
      return '24px';
    }
    if (value > 9) {
      return '28px';
    }
    return '32px';
  }};
  top: 50%;
  transform: translateY(-50%);
  font-size: 15px;
  color: ${({ theme, $isInputFocused }) =>
    theme.darkGray + ($isInputFocused ? '80' : '')};
  pointer-events: none;
  line-height: 18px;
`;

export const AdminInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  AdminInputComponentProps
>(function AdminInput(
  {
    onXButtonClick,
    shadowDesign,
    search,
    $textCenter,
    $password,
    $textareaHeight,
    $resizable,
    suffix,
    maxWidth,
    maxWidthSM,
    mb,
    label,
    tooltip,
    tooltipWidth = '500px',
    tooltipWidthSM = '95%',
    maxLength,
    noMaxLengthLabel,
    showCharCount,
    ...props
  },
  ref
) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string>('');
  const [isInputFocused, setIsInputFocused] = useState(false);

  // a11y: stable id linking the visible <Label> to the underlying input
  // via <label htmlFor>. Lets screen readers announce the label when the
  // input is focused, and lets clicks on the label focus the input.
  // Caller-supplied `id` (via {...props}) wins.
  const generatedId = useId();
  const inputId = (props as { id?: string }).id ?? generatedId;

  const isFileInput = props.type === 'file';

  // For file inputs, we need to exclude 'value' prop as it's not allowed
  const fileInputProps = isFileInput
    ? (() => {
        const { value, ...rest } = props as InputProps;
        return rest;
      })()
    : (props as InputProps);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
    } else {
      setFileName('');
    }

    // Call the original onChange if provided
    if (props.onChange && isFileInput) {
      (props.onChange as React.ChangeEventHandler<HTMLInputElement>)(e);
    }
  };

  return (
    <Div
      $relative
      $width="100%"
      $maxWidth={maxWidth}
      $maxWidthSM={maxWidthSM}
      $mb={mb}
    >
      {label && (
        <Label
          label={label}
          tooltip={tooltip}
          $textCenter={$textCenter}
          htmlFor={inputId}
        />
      )}
      {props.type === 'textarea' ? (
        <AdminTextAreaStyled
          id={inputId}
          ref={ref as React.Ref<HTMLTextAreaElement>}
          {...(props as TextareaProps)}
          $textCenter={$textCenter}
          $textareaHeight={$textareaHeight}
          $resizable={$resizable}
          shadowDesign={shadowDesign}
        />
      ) : isFileInput ? (
        !props.value ? (
          <FileInputWrapper>
            <FileInputStyled
              id={inputId}
              {...fileInputProps}
              ref={(el) => {
                (
                  fileInputRef as React.MutableRefObject<HTMLInputElement | null>
                ).current = el;
                if (typeof ref === 'function') ref(el);
                else if (ref)
                  (
                    ref as React.MutableRefObject<HTMLInputElement | null>
                  ).current = el;
              }}
              type="file"
              onChange={handleFileChange}
            />

            <Button $w="100%" $selected>
              Upload file
            </Button>
          </FileInputWrapper>
        ) : (
          <a
            href={
              typeof props.value === 'string'
                ? props.value
                : URL.createObjectURL(props.value as unknown as Blob)
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              $w="100%"
              $selected
              $xButton
              onXButtonClick={(e) => onXButtonClick?.(e)}
            >
              See current uploaded file here
            </Button>
          </a>
        )
      ) : suffix ? (
        <SuffixWrapper>
          <AdminInputStyled
            id={inputId}
            ref={ref as React.Ref<HTMLInputElement>}
            {...(props as InputProps)}
            $password={$password}
            $textCenter={$textCenter}
            $textareaHeight={$textareaHeight}
            search={!!search}
            shadowDesign={shadowDesign}
            $hasSuffix={!!suffix}
            onFocus={(e) => {
              setIsInputFocused(true);
              (props as InputProps).onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsInputFocused(false);
              (props as InputProps).onBlur?.(e);
            }}
          />
          <SuffixSpan
            $value={(props.value as string) ?? ''}
            $isInputFocused={isInputFocused}
          >
            {suffix}
          </SuffixSpan>
        </SuffixWrapper>
      ) : (
        <AdminInputStyled
          id={inputId}
          ref={ref as React.Ref<HTMLInputElement>}
          {...(props as InputProps)}
          $password={$password}
          $textCenter={$textCenter}
          $textareaHeight={$textareaHeight}
          search={!!search}
          shadowDesign={shadowDesign}
        />
      )}
      {maxLength && !noMaxLengthLabel && (
        <Disclaimer
          style={{ position: 'absolute', right: 12, bottom: -16 }}
          $m="0"
          $mt="4"
          $right
          $color={
            props.value?.toString().length &&
            props.value?.toString().length > maxLength
              ? lightTheme.primary
              : lightTheme.darkGray
          }
        >
          {props.value?.toString().length}/{maxLength}
        </Disclaimer>
      )}

      {showCharCount && !maxLength && !!props.value?.toString().length && (
        <Disclaimer
          style={{ position: 'absolute', right: 12, bottom: -16 }}
          $m="0"
          $mt="4"
          $right
          $color={lightTheme.darkGray}
        >
          {props.value?.toString().length}
        </Disclaimer>
      )}

      {search && (
        <Icon style={{ position: 'absolute', right: 24, top: 10 }}>
          <InputSearchIcon />
        </Icon>
      )}
    </Div>
  );
});
